import type { RegistryItemType } from "schemas/generated/registry";

import * as React from "react";

import { Empty } from "@replohq/lattice/components/empty/Empty";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import { cn } from "@replohq/lattice/utils/cn";
import { FileQuestion } from "lucide-react";
import useMeasure from "react-use-measure";

type PreviewRegistryItem = {
  type: RegistryItemType;
  name: string;
  previewHtml: string | null;
  previewUrls: string[];
};

type RegistryItemPreviewProps = {
  item: PreviewRegistryItem | null;
  isLoading: boolean;
  isError: boolean;
  className?: string;
  htmlViewportWidth?: number;
  disableHtmlScroll?: boolean;
};

const DISABLE_PREVIEW_TEXT_SELECTION_CSS =
  "*,html,body{-webkit-user-select:none!important;user-select:none!important}";
// NOTE (Ezra, 2026-07-10, REPL-29127): Static preview links and forms can navigate the iframe, so disable their pointer events while leaving the document scrollable.
const DISABLE_PREVIEW_POINTER_EVENTS_CSS =
  "*{pointer-events:none!important}html,body{pointer-events:auto!important}";
const DISABLE_PREVIEW_SCROLL_CSS =
  "html,body{overflow:hidden!important;overscroll-behavior:none!important}*,html,body{scrollbar-width:none!important;-ms-overflow-style:none!important}*::-webkit-scrollbar,html::-webkit-scrollbar,body::-webkit-scrollbar{display:none!important;width:0!important;height:0!important}";
const HTML_PREVIEW_EDGE_BLEED_PIXELS = 1;

// NOTE (Patrick, 2026-07-20): Registry previews can be either legacy screenshots or live deployed pages, which require different browser elements.
const IMAGE_PREVIEW_URL_PATTERN = /\.(png|jpe?g|gif|webp|avif|svg)(?:[?#]|$)/i;

function isImagePreviewUrl(url: string): boolean {
  return IMAGE_PREVIEW_URL_PATTERN.test(url);
}

function getSrcDocWithInjectedStyle({
  srcDoc,
  styleAttributeName,
  css,
}: {
  srcDoc: string;
  styleAttributeName: string;
  css: string;
}) {
  const style = `<style ${styleAttributeName}>${css}</style>`;
  if (/<head\b[^>]*>/i.test(srcDoc)) {
    return srcDoc.replace(/<head\b([^>]*)>/i, `<head$1>${style}`);
  }

  return `${style}${srcDoc}`;
}

function getSrcDocWithDisabledTextSelection(srcDoc: string) {
  return getSrcDocWithInjectedStyle({
    srcDoc,
    styleAttributeName: "data-replo-disable-text-selection",
    css: DISABLE_PREVIEW_TEXT_SELECTION_CSS,
  });
}

function getSrcDocWithDisabledPointerEvents(srcDoc: string) {
  return getSrcDocWithInjectedStyle({
    srcDoc,
    styleAttributeName: "data-replo-disable-pointer-events",
    css: DISABLE_PREVIEW_POINTER_EVENTS_CSS,
  });
}

function getSrcDocWithDisabledScroll(srcDoc: string) {
  return getSrcDocWithInjectedStyle({
    srcDoc,
    styleAttributeName: "data-replo-disable-scroll",
    css: DISABLE_PREVIEW_SCROLL_CSS,
  });
}

function getNoPreviewDescription({ item }: { item: PreviewRegistryItem }) {
  if (item.type === "skill" || item.type === "plan") {
    return `${item.name} doesn't have a visual preview yet. You can still use it in Replo.`;
  }
  return `${item.name} doesn't have a preview yet. You can still use it in Replo.`;
}

export function RegistryItemPreview({
  item,
  isLoading,
  isError,
  className,
  htmlViewportWidth,
  disableHtmlScroll = false,
}: RegistryItemPreviewProps) {
  const [htmlViewportRef, htmlViewportDimensions] = useMeasure({
    offsetSize: true,
  });
  const htmlViewportScale =
    htmlViewportWidth && htmlViewportDimensions.width > 0
      ? (htmlViewportDimensions.width + HTML_PREVIEW_EDGE_BLEED_PIXELS) /
        htmlViewportWidth
      : 1;

  if (isLoading) {
    return <Skeleton className={cn("h-full w-full", className)} />;
  }

  if (isError) {
    return (
      <Empty
        title="Something went wrong"
        description="This shared item could not load. Try refreshing the page."
        icon={<FileQuestion />}
        UNSAFE_className={cn("bg-sheet", className)}
      />
    );
  }

  if (!item) {
    return (
      <Empty
        title="Shared item not found"
        description="This shared item could not be found."
        icon={<FileQuestion />}
        UNSAFE_className={cn("bg-sheet", className)}
      />
    );
  }

  if (item.previewHtml) {
    const srcDocWithDisabledInteraction = getSrcDocWithDisabledPointerEvents(
      getSrcDocWithDisabledTextSelection(item.previewHtml),
    );
    const srcDoc = disableHtmlScroll
      ? getSrcDocWithDisabledScroll(srcDocWithDisabledInteraction)
      : srcDocWithDisabledInteraction;

    if (htmlViewportWidth) {
      return (
        <div
          ref={htmlViewportRef}
          className={cn("h-full w-full overflow-hidden bg-white", className)}
        >
          <div
            className="origin-top-left overflow-hidden bg-white"
            style={{
              height: `${100 / htmlViewportScale}%`,
              transform: `scale(${htmlViewportScale})`,
              width: htmlViewportWidth,
            }}
          >
            <iframe
              srcDoc={srcDoc}
              className="h-full w-full border-0 bg-white"
              // NOTE (Patrick, 2026-07-20): Registry HTML is a static snapshot and must remain scriptless.
              sandbox=""
              scrolling={disableHtmlScroll ? "no" : "auto"}
              title={item.name}
            />
          </div>
        </div>
      );
    }

    return (
      <iframe
        srcDoc={srcDoc}
        className={cn("h-full w-full border-0 bg-white", className)}
        // NOTE (Patrick, 2026-07-20): Registry HTML is a static snapshot and must remain scriptless.
        sandbox=""
        scrolling={disableHtmlScroll ? "no" : "auto"}
        title={item.name}
      />
    );
  }

  const previewUrl = item.previewUrls[0] ?? null;
  if (previewUrl) {
    if (isImagePreviewUrl(previewUrl)) {
      return (
        <img
          src={previewUrl}
          alt={item.name}
          className={cn(
            "h-full w-full select-none bg-white object-contain",
            className,
          )}
          draggable={false}
        />
      );
    }
    return (
      // NOTE (Ezra, 2026-07-10, REPL-29127): Cross-origin page previews cannot receive injected CSS, so the iframe itself must reject pointer interaction.
      <iframe
        src={previewUrl}
        className={cn(
          "pointer-events-none h-full w-full select-none border-0 bg-white",
          className,
        )}
        sandbox="allow-scripts allow-same-origin"
        scrolling="no"
        tabIndex={-1}
        title={item.name}
      />
    );
  }

  return (
    <Empty
      title="No preview"
      description={getNoPreviewDescription({ item })}
      icon={<FileQuestion />}
      UNSAFE_className={cn("bg-sheet", className)}
    />
  );
}
